import { useEffect, useContext, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import Utils from "../Utils";
import axios from "axios";
import _ from "lodash";
import { RecceTab } from "../components/common/index";
import AttachmentDiscussion from "../components/discussion/AttachmentDiscussion";
import FinalDocumentTab from "../components/hotelRepresentative/FinalDocumentTab";
import PhFinalDocumentTab from "../components/photographer/PhFinalDocumentTab";
import Shotlists from "../components/discussion/Shotlists";
import PrRequestUpload from "../components/photographer/PrRequestUpload";
import Swal from "sweetalert2";
import UserContext from "../contexts/UserContext";
import Loader from "../components/Loader";

const PhotoshootDetails = (props) => {
  const { id } = useParams();
  const fileInputRef = useRef(null);
  const fileInputRefPh = useRef(null);
  const [filter, setFilter] = useState({ pr_status: 0 }); // 0 = No Pr, 1 = PR Requested, 2 = PR Ready
  const [prReadyIds, setPrReadyIds] = useState([]);
  const [sendingPr, setSendingPr] = useState(false);
  const { siteStatuses } = useContext(UserContext);
  const [loading, setLoading] = useState(true);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* Photoshoot Images Listing */
  const [photoshootImages, setPhotoshootImages] = useState([]);
  const [photoshootFiles, setPhotoshootFiles] = useState([]);
  const updatePhotoshootImages = () => {
    axios
      .get(Utils.apiUrl(`photoshoot/photos?psid=${id}`), Utils.apiHeaders())
      .then((res) => {
        if (res.data.success) {
          setPhotoshootImages(res.data.data);
          setPhotoshootFiles(_.groupBy(res.data.files, "photo_ids"));
        }
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while loading the photoshoot details.",
        });
      });
  };

  /* Classes for specific status */
  const statusClasses = {
    Pending: "status-pill inline-block bg-pink-100 text-pink-700",
    Completed: "status-pill inline-block bg-green-200 text-green-700",
  };

  /* Recce Images Listing */
  const [recceImages, setRecceImages] = useState([]);
  const updateRecceImages = () => {
    axios
      .get(
        Utils.apiUrl(`photoshoot/recces/${id}?limit=9999`),
        Utils.apiHeaders(),
      )
      .then((res) => {
        console.log("Recce Images:");
        console.log(res.data);
        if (res.data.success) {
          setRecceImages(res.data.data);
        }
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "An error occurred while loading the recce images.",
        });
      });
  };

  const [photoshootDetails, setPhotoshootDetails] = useState({});
  const fetchPhotoshootDetails = () => {
    axios
      .get(Utils.apiUrl(`photoshoot/${id}`), Utils.apiHeaders())
      .then((res) => {
        if (_.get(res, "data.id", 0) == id) {
          setPhotoshootDetails(res.data);
          //updateRecceImages();
          updatePhotoshootImages();
          setLoading(false);
        } else {
          Swal.fire({
            icon: "error",
            title: "Invalid Photoshoot",
            text: "The photoshoot details could not be loaded. It may have been deleted or you may not have permission to view it.",
            showConfirmButton: true,
            showDenyButton: false,
            showCancelButton: false,
            confirmButtonText: "Go Back to Dashboard",
            allowOutsideClick: false,
          }).then((res) => {
            if (res.isConfirmed) {
              window.location.href = "/dashboard";
            }
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            err.response?.data?.message ||
            "An error occurred while loading the photoshoot details.",
        });
      });
  };

  //const [hotelDetails, setHotelDetails] = useState([]);
  useEffect(() => {
    /* Getting Photoshoot Details */
    fetchPhotoshootDetails();
  }, []);

  const [activeTab, setActiveTab] = useState("details");
  const [activePsTab, setActivePsTab] = useState("approved");

  const canShotList = () =>
    photoshootDetails.status >= 1 && photoshootDetails.status < 7;

  const isSDeckAvailable = () => photoshootDetails.lifestyle > 0;

  const canSDeck = () =>
    isSDeckAvailable() &&
    photoshootDetails.status >= 1 &&
    photoshootDetails.status <= 4 &&
    photoshootDetails.more_sdeck;

  const isRecceAvailable = () =>
    photoshootDetails.architectural > 0 &&
    ["new", "established"].includes(photoshootDetails.hotel_type);

  const canPSRecceUpload = () =>
    isRecceAvailable() &&
    photoshootDetails.status >= 1 &&
    photoshootDetails.status < 7 &&
    photoshootDetails.more_recce;

  const canViewPhotos = () =>
    (photoshootDetails.status >= 6 && photoshootDetails.status <= 8) ||
    (!isSDeckAvailable() && !isRecceAvailable());

  const canUploadPhotos = () =>
    (((photoshootDetails.type != "GEN" &&
      (photoshootDetails.hotel_representative_id == 0 || Utils.isHR())) ||
      (photoshootDetails.type === "GEN" &&
        (Utils.isPhotographer() ||
          (Utils.isHotelOwner() &&
            Number(_.get(photoshootDetails, "photographer_id", 0)) <= 0)))) &&
      [7, 8].includes(_.toNumber(photoshootDetails.status))) ||
    (Utils.isPhotographer() &&
      !isRecceAvailable() &&
      photoshootDetails.status < 9);

  const canUploadFinalDocs = () =>
    ((photoshootDetails.type != "GEN" &&
      (photoshootDetails.hotel_representative_id == 0 || Utils.isHR())) ||
      (photoshootDetails.type === "GEN" &&
        (Utils.isPhotographer() ||
          (Utils.isHotelOwner() &&
            Number(_.get(photoshootDetails, "photographer_id", 0)) <= 0)))) &&
    [9].includes(_.toNumber(photoshootDetails.status));

  const psApproved = () => _.toNumber(photoshootDetails.status) >= 9;

  useEffect(() => {
    if (photoshootDetails.status <= 2) {
      setActiveTab("shotlist");
    } else if (photoshootDetails.status <= 4) {
      setActiveTab("sdeck");
    } else if (
      (photoshootDetails.status <= 6 && isRecceAvailable()) ||
      (photoshootDetails.status < 7 &&
        Utils.isHR() &&
        getPSImages(["bt-approved"]).length === 0)
    ) {
      setActiveTab("recce");
    } else if (
      photoshootDetails.status < 9 &&
      (canViewPhotos() || canUploadPhotos())
    ) {
      setActiveTab("photoshoot");
    } else if (psApproved()) {
      //setActivePsTab("approved");
      setActiveTab("finaldocs");
    }
  }, [photoshootDetails]);

  const approvedImagesWithPr = (pr_status) => {
    return photoshootImages.filter(
      (item) =>
        item.status === "bt-approved" &&
        (pr_status == 0 || item.pr_status === pr_status),
    );
  };

  const getPSImages = (statuses) => {
    return photoshootImages.filter((item) => statuses.includes(item.status));
  };

  const submitUploadedToBT = () => {
    Swal.fire({
      title: "Are you sure?",
      text: `All uploaded images will be submitted to the Brand Team for review!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#047857",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, send to Brand Team!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            Utils.apiUrl(`photoshoot/photos/submitAll`),
            { psId: id },
            Utils.apiHeaders(),
          )
          .then((res) => {
            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Sent!",
                text:
                  res.data.message ||
                  `All images are sent to Brand Team successfully`,
                timer: 2000,
                showConfirmButton: false,
              });
              updatePhotoshootImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Send Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          });
      }
    });
  };

  const deleteAllUploaded = () => {
    Swal.fire({
      title: "Are you sure?",
      text: `All uploaded images will be permanently deleted!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete all!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(
            Utils.apiUrl(`photoshoot/photos/all/${id}`),
            Utils.apiHeaders(),
          )
          .then((res) => {
            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text:
                  res.data.message ||
                  `All uploaded images deleted successfully`,
                timer: 2000,
                showConfirmButton: false,
              });
              updatePhotoshootImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          });
      }
    });
  };

  const showStatuses = (photoshootDetails) => {
    let showStatuses = [1]; //shotlist

    if (photoshootDetails.type == "GEN") {
      if (photoshootDetails.lifestyle > 0) {
        showStatuses.push(5); // styling deck
      }
      if (
        photoshootDetails.architectural > 0 &&
        ["new", "established"].includes(photoshootDetails.hotel_type)
      ) {
        showStatuses.push(7); // recce deck
      }
      showStatuses.push(9, 11); // photoshoot and final docs
    } else {
      //=== for REN or LA
      showStatuses = [9, 11]; // photoshoot and final docs
    }
    return (
      <div className="flex flex-col items-start space-y-2">
        {_.map(showStatuses, (s, idx) => (
          <div
            key={idx}
            className={`${statusClasses[s > photoshootDetails.status ? "Pending" : "Completed"]} inline w-full text-left`}
          >
            <i
              className={`fa-solid fa-${s <= photoshootDetails.status ? "check-circle" : "circle"}`}
            ></i>{" "}
            Status: {siteStatuses.status[s]}
          </div>
        ))}
      </div>
    );
  };

  const renderMainTabs = () => {
    const status = photoshootDetails.status;
    let tabs = {};

    if (photoshootDetails.type === "GEN") {
      tabs = {
        shotlist: ["Shot-list", status >= 1],
      };
      if (photoshootDetails.lifestyle > 0) {
        tabs["sdeck"] = ["Moodboard", status >= 1, "Lifestyle/Styling Deck"];
      }
      if (
        photoshootDetails.architectural > 0 &&
        ["new", "established"].includes(photoshootDetails.hotel_type)
      ) {
        tabs["recce"] = ["Recce", status >= 1];
      }
    }
    tabs["photoshoot"] = [
      "Post Production Image Review",
      status >= 7 ||
        (status < 5 && !isSDeckAvailable() && !isRecceAvailable()) ||
        (status < 7 && !isRecceAvailable()),
    ];
    tabs["finaldocs"] = ["Download Final Images and Document", status >= 9];

    return (
      <div className="flex space-x-1">
        {_.map(tabs, (tab, key) => (
          <button
            className={`tab-btn ${tab[1] && activeTab === key ? "active" : "bg-gray-100"} ${!tab[1] ? "cursor-not-allowed opacity-50" : ""}`}
            data-tab={key}
            onClick={() => (tab[1] ? setActiveTab(key) : null)}
          >
            {tab[0]}
            {tab[2] && <div className="ml-1 text-xs">{`(${tab[2]})`}</div>}
          </button>
        ))}
      </div>
    );
  };

  const handlePrReadyChange = (photoId) => {
    setPrReadyIds((prev) => {
      if (prev.includes(photoId)) {
        return prev.filter((id) => id !== photoId);
      } else {
        return [...prev, photoId];
      }
    });
  };

  const sendPrReady = () => {
    if (prReadyIds.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Images Selected",
        text: "Please select at least one image",
      });
      return;
    }

    Swal.fire({
      title: "Request PR?",
      text: "Are you sure you want to request PR for selected images?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Send",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setSendingPr(true);

      axios
        .post(
          Utils.apiUrl("photoshoot/pr-request"),
          { photoIds: prReadyIds, psid: photoshootDetails.id },
          Utils.apiHeaders(),
        )
        .then((res) => {
          if (res.data.success) {
            Swal.fire({
              icon: "success",
              title: "PR Requested",
              text: "PR requested successfully",
              timer: 2000,
              showConfirmButton: false,
            });

            setPrReadyIds([]);
            updatePhotoshootImages();
          } else {
            Swal.fire({
              icon: "error",
              title: "Failed",
              text: res.data.message || "Failed to request PR",
            });
          }
        })
        .catch((err) => {
          console.error(err);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Something went wrong while sending PR request",
          });
        })
        .finally(() => {
          setSendingPr(false);
        });
    });
  };

  // Drag and drop handlers for Photoshoot Images
  const handleDragOverPh = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeavePh = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropPh = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;

    if (files.length > 0) {
      Swal.fire({
        icon: "info",
        title: "Files Selected",
        text: `${files.length} file(s) ready to upload`,
        timer: 1500,
        showConfirmButton: false,
      });

      fileInputRefPh.current.files = files;

      fileInputRefPh.current.dispatchEvent(
        new Event("change", { bubbles: true }),
      );
    }
  };

  const [psLoading, setPsLoading] = useState(``);

  const handleFileChangePh = (e) => {
    const files = e.target.files;

    if (files.length > 0) {
      const formDataPh = new FormData();

      for (let i = 0; i < files.length; i++) {
        formDataPh.append("photos", files[i]);
      }

      // Show loader
      Swal.fire({
        title: "Uploading...",
        text: "Please wait while files are uploading",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      axios
        .post(
          Utils.apiUrl(`photoshoot/photos?psid=${id}`),
          formDataPh,
          Utils.apiHeaders(),
        )
        .then((res) => {
          Swal.close();

          if (res.data.success) {
            setActivePsTab("uploaded");
            updatePhotoshootImages();

            Swal.fire({
              icon: "success",
              title: "Uploaded!",
              text: res.data.message,
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Upload Failed",
              text: res.data.message,
            });
          }
        })
        .catch((err) => {
          console.error("Upload failed:", err);
          Swal.close();

          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to upload files",
          });
        });
    }
  };

  const handleClickPh = () => {
    Swal.fire({
      icon: "info",
      title: "Select Files",
      text: "Choose images to upload",
      timer: 1200,
      showConfirmButton: false,
    });

    fileInputRefPh.current.click();
  };

  const deletePhotoshootFile = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This file will be deleted permanently",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(Utils.apiUrl(`photoshoot/photo/${id}`), Utils.apiHeaders())
          .then((res) => {
            if (res.data.success) {
              updatePhotoshootImages();

              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: res.data.message,
              });
            } else {
              Swal.fire({
                icon: "error",
                title: "Failed",
                text: res.data.message,
              });
            }
          })
          .catch(() => {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete file",
            });
          });
      }
    });
  };

  const getLatestImageVersion = (id, index) => {
    let item = photoshootImages.filter((img) => img.orig_photo_id === id);
    if (item.length > 0) {
      item = item.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      )[0];
    } else {
      item = photoshootImages.find((img) => img.id === id);
    }
    return item;
  };

  const renderLatestImageVersion = (id, index) => {
    let item = getLatestImageVersion(id, index);

    return (
      <>
        <img
          src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${item.fname}`}
          alt={item.origname}
          className="w-20 h-14 object-cover rounded mr-4"
        />
        <div className="flex-1">
          <div className="font-semibold text-gray-800">{item.origname}</div>
          {/* If BT Feedback is not blank */}
          {item.bt_feedback != null && item.bt_feedback != "" && (
            <div className="text-sm text-gray-600">{item.bt_feedback}</div>
          )}
          {/* If BT Feedback is blank showing AI Feedback */}
          {(item.bt_feedback == null || item.bt_feedback == "") &&
            item.ai_feedback != undefined && (
              <>
                <div className="text-sm text-gray-600">
                  {item.ai_feedback != "" &&
                    (() => {
                      try {
                        const parsed = JSON.parse(item.ai_feedback);
                        return parsed.feedback.map((feedback, index) => (
                          <p key={index}>{feedback}</p>
                        ));
                      } catch (e) {
                        return <p>Invalid AI feedback</p>;
                      }
                    })()}
                </div>
              </>
            )}
        </div>
      </>
    );
  };

  const canRequestPR = () => {
    return (
      (photoshootDetails.hotel_type !== "refresh" &&
        photoshootDetails.type === "GEN" &&
        Utils.isHR()) ||
      ((Utils.isAMT() || Utils.isHotelOwner()) &&
        _.get(photoshootDetails, "hotel_representative_id", 0) <= 0 &&
        _.get(photoshootDetails, "hotel_id", 0) == Utils.getSelectedHotelId())
    );
  };

  const getEditableImages = () => {
    return photoshootImages.filter((item) =>
      ["bt-rejected"].some(
        (status) => item.status.includes(status) && item.orig_photo_id == null,
      ),
    );
  };

  const downloadPhotoPdf = () => {
    Swal.fire({
      title: "Download PDF?",
      text: `Photoshoot_${id}_photos.pdf will be downloaded`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, download",
    }).then((result) => {
      if (result.isConfirmed) {
        // 🔄 Show loader
        Swal.fire({
          title: "Downloading...",
          text: "Please wait while your PDF is being prepared",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        axios
          .get(Utils.apiUrl(`photoshoot/downloadPdf/photos/${id}`), {
            ...Utils.apiHeaders(),
            responseType: "blob", // important (your earlier code had it wrong place)
          })
          .then((res) => {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `photoshoot_${id}_photos.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
          })
          .then(() => {
            // ✅ Show success alert after download
            Swal.fire({
              icon: "success",
              title: "Downloaded!",
              text: "PDF downloaded successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((err) => {
            console.error(err);

            // ERROR ALERT
            Swal.fire({
              icon: "error",
              title: "Download Failed",
              text: "Unable to download PDF. Please try again.",
            });
          });
      }
    });
  };

  const getPhotoshootTypeName = (record) => {
    let type = record.type;
    if (record.type === "GEN") {
      if (record.lifestyle > 0 && record.architectural === 0) type = "LIF";
      else if (record.lifestyle == 0 && record.architectural > 0) type = "ARC";
      else if (record.lifestyle == 0 && record.architectural == 0) type = "PPI";
    }
    return Utils.getPhotoshootTypeName(type);
  };

  return (
    <>
      <div className="container mx-auto p-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">
          Photoshoot Details
        </h1>
        <div className="bg-white rounded-lg shadow p-6">
          {loading && <Loader />}
          {!loading && (
            <>
              <div className="flex flex-col lg:flex-row lg:justify-between mb-6">
                <div className="text-left flex-1">
                  <h2 className="text-2xl font-semibold mb-3">
                    {photoshootDetails.title}
                    <a
                      href={`https://marriott.com/${photoshootDetails.marsha}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Link to Marriott"
                    >
                      <i className="fa-solid fa-link px-1"></i>
                    </a>
                    <span className="mx-1">, {photoshootDetails.cname}</span>
                    <div className="text-sm text-green-600">
                      Initiated by:{" "}
                      {Utils.userTypeName(photoshootDetails.requester_type)}
                    </div>
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 mb-4">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">Type</span>
                        <span className="font-medium text-gray-800">
                          {Utils.getPhotoshootHotelTypeName(
                            photoshootDetails.hotel_type,
                          )}
                        </span>
                      </div>

                      {photoshootDetails.type === "GEN" && (
                        <div className="grid grid-cols-2 gap-2">
                          <span className="text-sm text-gray-500">
                            Photographer
                          </span>
                          <span className="font-medium text-gray-800">
                            {photoshootDetails.pgname}
                          </span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">
                          Hotel Photography Lead
                        </span>
                        <span className="font-medium text-gray-800">
                          {photoshootDetails.hrname}
                        </span>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-2">
                        <span className="text-sm text-gray-500">
                          Shoot Date
                        </span>
                        <span className="font-medium text-gray-800">
                          {formatDate(photoshootDetails.date_from)}
                          {!_.isNull(photoshootDetails.date_to) && (
                            <>- {formatDate(photoshootDetails.date_to)}</>
                          )}
                        </span>
                      </div>

                      {!_.isNull(photoshootDetails.recce_end) && (
                        <div className="grid grid-cols-2 gap-2">
                          <span className="text-sm text-gray-500">
                            Recce Completed By
                          </span>
                          <span className="font-medium text-gray-800">
                            {photoshootDetails.recce_end
                              ? formatDate(photoshootDetails.recce_end)
                              : "-"}
                          </span>
                        </div>
                      )}

                      {!_.isNull(photoshootDetails.delivery_date) && (
                        <div className="grid grid-cols-2 gap-2">
                          <span className="text-sm text-gray-500">
                            Completion Date
                          </span>
                          <span className="font-medium text-gray-800">
                            {photoshootDetails.delivery_date
                              ? formatDate(photoshootDetails.delivery_date)
                              : "-"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold bg-gray-100 text-xl text-gray-500 mb-2 text-center text-capitalize px-4 py-2 inline-block rounded-xl">
                    {getPhotoshootTypeName(photoshootDetails)}
                  </div>
                  {showStatuses(photoshootDetails)}
                </div>
              </div>
              {/* <p className="text-sm text-gray-500 mt-1 text-right"><span className="font-medium text-gray-800">{photoshootDetails.status_label}</span></p> */}

              {/* <!-- Tabs --> */}
              <div className="mt-6">
                {renderMainTabs()}

                <div className="tab-content">
                  <div className="tab-panel">
                    {/* <!-- Tab: Shotlists --> */}
                    {activeTab === "shotlist" && (
                      <Shotlists
                        psdetails={photoshootDetails}
                        attachments={photoshootDetails.attachments}
                        psId={id}
                        canUpload={canShotList}
                        onUpload={fetchPhotoshootDetails}
                      />
                    )}

                    {/* <!-- Tab: Photoshoot Styling Deck --> */}
                    {activeTab === "sdeck" && (
                      <RecceTab
                        mode="sdeck"
                        psId={id}
                        canUpload={canSDeck}
                        onUploadRecce={(dets) =>
                          setPhotoshootDetails({
                            ...photoshootDetails,
                            ...dets,
                          })
                        }
                      />
                    )}

                    {/* <!-- Tab: Photoshoot Recce --> */}
                    {activeTab === "recce" && (
                      <RecceTab
                        mode="recce"
                        psId={id}
                        canUpload={canPSRecceUpload}
                        onUploadRecce={(dets) =>
                          setPhotoshootDetails({
                            ...photoshootDetails,
                            ...dets,
                          })
                        }
                      />
                    )}

                    {/* <!-- Tab: Photoshoot Images --> */}
                    {activeTab === "photoshoot" && (
                      <>
                        {canUploadPhotos() && (
                          <>
                            {isRecceAvailable() && (
                              <div className="text-sm bg-blue-100 rounded p-1 text-blue-800 mb-2">
                                <i className="fa fa-info-circle mr-2"></i>
                                If final recce deck has not been downloaded,
                                please switch to the recce tab to download the
                                final recce deck review.
                              </div>
                            )}
                            <div className="text-sm bg-yellow-100 rounded p-1 text-gray-800 mb-2">
                              <i className="fa fa-info-circle mr-2"></i>For Post
                              Production review, upload only up to 1 MB per
                              image
                            </div>
                            <div
                              id="drop-zone"
                              className="drop-zone"
                              onClick={handleClickPh}
                              onDragOver={handleDragOverPh}
                              onDragLeave={handleDragLeavePh}
                              onDrop={handleDropPh}
                            >
                              Drag &amp; Drop files here or click to select
                              <input
                                name="photos"
                                type="file"
                                id="file-input"
                                multiple="multiple"
                                className="hidden"
                                accept="image/*"
                                ref={fileInputRefPh}
                                onChange={handleFileChangePh}
                              />
                            </div>
                          </>
                        )}

                        <div className="mt-2">
                          {/* Sub Tabs */}

                          <div className="">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                              <div>
                                {(Utils.isPhotographer() ||
                                  Utils.isHotelOwner() ||
                                  photoshootDetails.type !== "GEN") &&
                                  !psApproved() && (
                                    <div
                                      className="mt-8 flex space-x-1"
                                      role="tablist"
                                      aria-label="Files Tabs"
                                    >
                                      <button
                                        className={`files-tab-btn2 px-4 py-2 rounded bg-gray-100 font-semibold ${activePsTab === "uploaded" ? "active" : "bg-gray-100"}`}
                                        data-tab="uploaded"
                                        onClick={() =>
                                          setActivePsTab("uploaded")
                                        }
                                      >
                                        Uploaded & Submitted
                                        <span className="ml-1 text-sm">
                                          (
                                          {
                                            photoshootImages.filter((item) =>
                                              [
                                                "uploaded",
                                                "submitted",
                                                "ai-processed",
                                              ].some(
                                                (status) =>
                                                  item.status.includes(
                                                    status,
                                                  ) &&
                                                  item.orig_photo_id == null,
                                              ),
                                            ).length
                                          }
                                          )
                                        </span>
                                      </button>
                                      <button
                                        className={`files-tab-btn2 px-4 py-2 rounded bg-gray-100 font-semibold ${activePsTab === "edits" ? "active" : "bg-gray-100"}`}
                                        data-tab="edits"
                                        onClick={() => setActivePsTab("edits")}
                                      >
                                        Edits Needed (To Review)
                                        <span className="ml-1 text-sm">
                                          (
                                          {
                                            photoshootImages.filter((item) =>
                                              ["bt-rejected"].some(
                                                (status) =>
                                                  item.status.includes(
                                                    status,
                                                  ) &&
                                                  item.orig_photo_id == null,
                                              ),
                                            ).length
                                          }
                                          )
                                        </span>
                                      </button>
                                      <button
                                        className={`files-tab-btn2 px-4 py-2 rounded bg-gray-100 font-semibold ${activePsTab === "approved" ? "active" : "bg-gray-100"}`}
                                        data-tab="approved"
                                        onClick={() =>
                                          setActivePsTab("approved")
                                        }
                                      >
                                        Approved
                                        <span className="ml-1 text-sm">
                                          (
                                          {
                                            photoshootImages.filter(
                                              (item) =>
                                                item.status === "bt-approved",
                                            ).length
                                          }
                                          )
                                        </span>
                                      </button>
                                    </div>
                                  )}

                                {activePsTab === "approved" &&
                                  getPSImages(["bt-approved"]).length > 0 &&
                                  photoshootDetails.type === "GEN" && (
                                    <div
                                      className="mt-2 flex space-x-1"
                                      role="tablist"
                                      aria-label="Files Tabs"
                                    >
                                      {photoshootDetails.hotel_type !==
                                        "refresh" && (
                                        <>
                                          <button
                                            className={`files-tab-btn2 px-4 py-2 rounded bg-gray-100 font-semibold ${filter.pr_status === 2 ? "active" : "bg-gray-100"}`}
                                            onClick={() =>
                                              setFilter({ pr_status: 2 })
                                            }
                                          >
                                            PR Requested
                                            <span className="ml-1 text-sm">
                                              ({approvedImagesWithPr(2).length})
                                            </span>
                                          </button>
                                          <button
                                            className={`files-tab-btn2 px-4 py-2 rounded bg-gray-100 font-semibold ${filter.pr_status === 3 ? "active" : "bg-gray-100"}`}
                                            onClick={() =>
                                              setFilter({ pr_status: 3 })
                                            }
                                          >
                                            PR Ready
                                            <span className="ml-1 text-sm">
                                              ({approvedImagesWithPr(3).length})
                                            </span>
                                          </button>
                                        </>
                                      )}
                                      <button
                                        className={`files-tab-btn2 px-4 py-2 rounded bg-gray-100 font-semibold ${filter.pr_status === 0 ? "active" : "bg-gray-100"}`}
                                        onClick={() =>
                                          setFilter({ pr_status: 0 })
                                        }
                                      >
                                        Approved Images
                                        <span className="ml-1 text-sm">
                                          ({approvedImagesWithPr(0).length})
                                        </span>
                                      </button>
                                    </div>
                                  )}
                              </div>
                              {getPSImages(["bt-approved"]).length > 0 && (
                                <div className="text-right text-sm">
                                  <div className="block">
                                    Total Images :{" "}
                                    <span>{photoshootImages.length}</span>
                                  </div>
                                  <div className="block">
                                    Revision Needed :{" "}
                                    <span>
                                      {
                                        photoshootImages.filter((item) =>
                                          ["bt-rejected"].some((status) =>
                                            item.status.includes(status),
                                          ),
                                        ).length
                                      }
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="tab-content2">
                            {Utils.isHR() && (
                              <div className="instruction-note mt-4 mb-4">
                                <i className="fas fa-lightbulb mr-2"></i>
                                Using the
                                <a
                                  href="https://mgscloud.marriott.com/mgs/marrdocs/mgs/common/salesmktgrevmgmt/marketing/photography/photoshoots/photographyworksheetdac.xlsx"
                                  download={true}
                                  className="text-red-400 underline px-2"
                                >
                                  DAC Worksheet (XLS)
                                </a>
                                , prepare the required asset metadata with
                                <a
                                  href="https://mgscloud.marriott.com/mgs/marrdocs/mgs/common/salesmktgrevmgmt/ecommerce/digitalassets/captionalttextwriting.pdf"
                                  download={true}
                                  className="text-red-400 underline px-2"
                                >
                                  Caption & Alt Text Writing Standards (PDF)
                                </a>
                                before uploading your assets to DAC.
                              </div>
                            )}

                            {!canUploadPhotos() &&
                              (photoshootImages.length === 0 ||
                                getPSImages(["bt-approved"]).length === 0) && (
                                <>
                                  <div className="instruction-note mt-4 mb-4">
                                    <i className="fas fa-lightbulb mr-2"></i>
                                    {photoshootImages.length === 0 && (
                                      <>
                                        Photographer has not uploaded any images
                                        yet. Please check back later.
                                      </>
                                    )}
                                    {photoshootImages.length > 0 &&
                                      getPSImages(["bt-approved"]).length ===
                                        0 && (
                                        <>
                                          Post production Images are in review.
                                          We will notify you once there are
                                          approved images by Brand Team.
                                        </>
                                      )}
                                    {isRecceAvailable() && (
                                      <p>
                                        If final recce deck has not been
                                        downloaded, please switch to the recce
                                        tab
                                      </p>
                                    )}
                                  </div>
                                </>
                              )}
                            {/* Uploaded Tab */}
                            {activePsTab === "uploaded" && (
                              <>
                                {getPSImages(["uploaded"]).length > 0 && (
                                  <div className="border border-gray-300 mb-4 p-2 rounded bg-gray-50">
                                    <h2 className="flex justify-between text-2xl font-semibold text-gray-700 mb-4">
                                      Uploaded Images (
                                      {getPSImages(["uploaded"]).length})
                                      <div className="flex text-sm justify-end mb-4 gap-2">
                                        <button
                                          className="rounded-md p-1 px-4 bg-green-700 text-white"
                                          onClick={submitUploadedToBT}
                                        >
                                          Submit to Brand Team for Review
                                        </button>
                                        <button
                                          className="rounded-md p-1 px-4 bg-red-700 text-white"
                                          onClick={deleteAllUploaded}
                                        >
                                          Delete all Uploaded Images
                                        </button>
                                      </div>
                                    </h2>
                                    <div
                                      id="file-grid"
                                      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4"
                                    >
                                      {/* <!-- Tiles will be appended here --> */}
                                      {getPSImages(["uploaded"]).map(
                                        (item, index) => (
                                          <>
                                            <div className="relative bg-white rounded-lg overflow-hidden shadow-sm border">
                                              <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
                                                <img
                                                  src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${item.fname}`}
                                                  alt=""
                                                  className="object-cover w-full h-full"
                                                />
                                              </div>
                                              <div className="p-3">
                                                <div
                                                  className="text-sm font-semibold text-gray-800 truncate"
                                                  title=""
                                                >
                                                  {item.origname}
                                                </div>
                                              </div>
                                              {item.status === "uploaded" && (
                                                <div className="absolute top-2 right-2 flex space-x-2">
                                                  <button
                                                    className="w-8 h-8 flex items-center justify-center rounded-full text-red-700"
                                                    aria-label="Mark approved"
                                                    title="Click to Delete this image"
                                                    onClick={() =>
                                                      deletePhotoshootFile(
                                                        item.id,
                                                      )
                                                    }
                                                  >
                                                    <i className="fa-solid fa-xl fa-times-circle"></i>
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          </>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}
                                {getPSImages(["submitted", "ai-processed"])
                                  .length > 0 && (
                                  <div className="border border-gray-300 mb-4 p-2 rounded bg-gray-50">
                                    <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                                      Submitted to Brand Team (
                                      {
                                        getPSImages([
                                          "submitted",
                                          "ai-processed",
                                        ]).length
                                      }
                                      )
                                    </h2>
                                    <div
                                      id="file-grid"
                                      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4"
                                    >
                                      {/* <!-- Tiles will be appended here --> */}
                                      {getPSImages([
                                        "submitted",
                                        "ai-processed",
                                      ]).map((item, index) => (
                                        <>
                                          <div className="relative bg-white rounded-lg overflow-hidden shadow-sm border">
                                            <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
                                              <img
                                                src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${item.fname}`}
                                                alt=""
                                                className="object-cover w-full h-full"
                                              />
                                            </div>
                                            <div className="p-3">
                                              <div
                                                className="text-sm font-semibold text-gray-800 truncate"
                                                title=""
                                              >
                                                {item.origname}
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Edits Needed Tab */}
                            {activePsTab === "edits" && (
                              <>
                                <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                                  Edits Needed
                                </h2>
                                {/* <div id="file-grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4"> */}
                                {/* <!-- Tiles will be appended here --> */}

                                {getEditableImages().length > 0 && (
                                  <>
                                    <div className="bg-blue-100 rounded p-1 mt-4 mb-4 text-xl">
                                      <i className="fas fa-lightbulb mr-2"></i>
                                      You can download Brand Team feedback as{" "}
                                      <button
                                        className="btn rounded-md bg-yellow-100 p-1 pr-2"
                                        onClick={downloadPhotoPdf}
                                      >
                                        <i className="fas fa-download mx-1"></i>
                                        PDF
                                      </button>
                                    </div>
                                    {getEditableImages().map((item, index) => (
                                      <>
                                        <ul className="space-y-4">
                                          <li className="flex items-center bg-white rounded-lg shadow-sm border p-3">
                                            {renderLatestImageVersion(
                                              item.id,
                                              index,
                                            )}
                                            <div>
                                              <a
                                                href={`${process.env.REACT_APP_BASENAME}view-feedback/${id}/${getLatestImageVersion(item.id, index).id}/${getLatestImageVersion(item.id, index).fname}`}
                                                className="inline-block items-center px-4 py-2 rounded btn"
                                              >
                                                <i className="fa-regular fa-eye"></i>{" "}
                                                View Details
                                              </a>
                                            </div>
                                          </li>
                                        </ul>
                                      </>
                                    ))}
                                  </>
                                )}
                                {/* </div> */}
                              </>
                            )}

                            {/* Approved Tab */}
                            {activePsTab === "approved" &&
                              getPSImages(["bt-approved"]).length > 0 && (
                                <div className="tab-content3">
                                  {(Utils.isHR() || Utils.isAMT()) &&
                                    filter.pr_status == 2 && (
                                      <>
                                        {approvedImagesWithPr(2).length > 0 && (
                                          <div className="rounded-md bg-yellow-100 px-4 py-3 text-sm text-yellow-700 mt-3">
                                            <strong>Note:</strong> Photographer
                                            has been notified about the PR
                                            requested, please wait for the
                                            photographer to upload High-Res Zip
                                            and their Usage Rights document.
                                          </div>
                                        )}
                                      </>
                                    )}

                                  {/* <!-- Tiles will be appended here --> */}
                                  {filter.pr_status === 2 &&
                                    approvedImagesWithPr(filter.pr_status)
                                      .length > 0 && (
                                      <>
                                        <div
                                          id="file-grid"
                                          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 pt-2"
                                        >
                                          {approvedImagesWithPr(
                                            filter.pr_status,
                                          ).map((item, index) => (
                                            <>
                                              <div className="relative bg-white rounded-lg overflow-hidden shadow-sm border">
                                                <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
                                                  <img
                                                    src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${item.fname}`}
                                                    alt=""
                                                    className="object-cover w-full h-full"
                                                  />
                                                </div>
                                                <div className="p-3">
                                                  <div
                                                    className="text-sm font-semibold text-gray-800 truncate"
                                                    title=""
                                                  >
                                                    {item.origname}
                                                  </div>
                                                </div>
                                                {canUploadPhotos() &&
                                                  parseInt(item.pr_status) ===
                                                    2 && (
                                                    <div className="p-3 pt-0">
                                                      <input
                                                        type="checkbox"
                                                        id={`pr_ready_${item.id}`}
                                                        checked={prReadyIds.includes(
                                                          item.id,
                                                        )}
                                                        onChange={() =>
                                                          handlePrReadyChange(
                                                            item.id,
                                                          )
                                                        }
                                                      />
                                                      <label
                                                        htmlFor={`pr_ready_${item.id}`}
                                                        className="ml-2"
                                                      >
                                                        PR Ready
                                                      </label>
                                                    </div>
                                                  )}
                                              </div>
                                            </>
                                          ))}
                                        </div>
                                        {canUploadPhotos() &&
                                          filter.pr_status === 2 && (
                                            <PrRequestUpload
                                              psid={id}
                                              prReadyIds={prReadyIds}
                                              onUploadSuccess={
                                                updatePhotoshootImages
                                              }
                                            />
                                          )}
                                      </>
                                    )}
                                  {filter.pr_status == 3 &&
                                    _.keys(photoshootFiles).length > 0 &&
                                    _.map(photoshootFiles, (psfiles, phids) => {
                                      phids = phids.includes("[")
                                        ? JSON.parse(phids)
                                        : phids
                                            .split(",")
                                            .map((id) => parseInt(id.trim()));
                                      return (
                                        <>
                                          <div
                                            id="file-grid"
                                            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 pt-2"
                                          >
                                            {approvedImagesWithPr(
                                              filter.pr_status,
                                            )
                                              .filter((item) =>
                                                phids.includes(
                                                  parseInt(item.id),
                                                ),
                                              )
                                              .map((item, index) => (
                                                <div
                                                  className="relative bg-white rounded-lg overflow-hidden shadow-sm border"
                                                  key={item.id}
                                                >
                                                  <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
                                                    <img
                                                      src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${item.fname}`}
                                                      alt=""
                                                      className="object-cover w-full h-full"
                                                    />
                                                  </div>
                                                  <div className="p-3">
                                                    <div
                                                      className="text-sm font-semibold text-gray-800 truncate"
                                                      title=""
                                                    >
                                                      {item.origname}
                                                    </div>
                                                  </div>
                                                </div>
                                              ))}
                                          </div>

                                          {filter.pr_status == 3 &&
                                            approvedImagesWithPr(
                                              filter.pr_status,
                                            ).filter((item) =>
                                              phids.includes(parseInt(item.id)),
                                            ).length > 0 &&
                                            psfiles.map((file) => (
                                              <a
                                                href={file.filename}
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center m-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                              >
                                                <i className="fas fa-download"></i>
                                                <span>
                                                  {file.filetype === 1
                                                    ? "Usage Rights"
                                                    : "High Res"}
                                                </span>
                                              </a>
                                            ))}
                                        </>
                                      );
                                    })}

                                  {filter.pr_status === 0 &&
                                    approvedImagesWithPr(filter.pr_status)
                                      .length > 0 && (
                                      <>
                                        <div
                                          id="file-grid"
                                          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4 pt-2"
                                        >
                                          {approvedImagesWithPr(
                                            filter.pr_status,
                                          ).map((item, index) => (
                                            <div
                                              className="relative bg-white rounded-lg overflow-hidden shadow-sm border"
                                              key={item.id}
                                            >
                                              <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
                                                <img
                                                  src={`${process.env.REACT_APP_API_URL}/photoshoot/photo/thumb/${item.fname}`}
                                                  alt=""
                                                  className="object-cover w-full h-full"
                                                />
                                              </div>

                                              <div className="p-3">
                                                <div className="text-sm font-semibold text-gray-800 truncate">
                                                  {item.origname}
                                                </div>
                                              </div>

                                              {/* Request PR checkbox */}
                                              {canRequestPR() &&
                                                parseInt(item.pr_status) ===
                                                  0 && (
                                                  <div className="p-3">
                                                    <input
                                                      type="checkbox"
                                                      checked={prReadyIds.includes(
                                                        item.id,
                                                      )}
                                                      onChange={() =>
                                                        handlePrReadyChange(
                                                          item.id,
                                                        )
                                                      }
                                                    />
                                                    <label className="ml-2">
                                                      Request PR
                                                    </label>
                                                  </div>
                                                )}
                                            </div>
                                          ))}
                                        </div>

                                        {prReadyIds.length > 0 && (
                                          <div className="text-right mt-4">
                                            <button
                                              onClick={sendPrReady}
                                              disabled={sendingPr}
                                              className={`btn text-white py-2 px-4 rounded ${sendingPr ? "opacity-50 cursor-not-allowed" : ""}`}
                                            >
                                              {sendingPr
                                                ? "Requesting..."
                                                : "Request PR"}
                                            </button>
                                          </div>
                                        )}
                                      </>
                                    )}
                                </div>
                              )}
                          </div>
                        </div>
                      </>
                    )}

                    {/* <!-- Tab: Final Documents --> */}
                    {activeTab === "finaldocs" && (
                      <>
                        {canUploadFinalDocs() && (
                          <PhFinalDocumentTab
                            photoshootDetails={photoshootDetails}
                            photoshootImages={photoshootImages}
                            finalDocs={_.filter(
                              _.flattenDeep(_.values(photoshootFiles)),
                              (f) => !f.isPR,
                            )}
                          />
                        )}
                        {!canUploadFinalDocs() && (
                          <>
                            <FinalDocumentTab
                              photoshootDetails={photoshootDetails}
                              photoshootImages={photoshootImages}
                              finalDocs={_.filter(
                                _.flattenDeep(_.values(photoshootFiles)),
                                (f) => !f.isPR,
                              )}
                            />
                            {Number(photoshootDetails.status) >= 11 &&
                              Utils.isHR() && (
                                <>
                                  <div className="instruction-note mt-4 mb-4">
                                    <i className="fas fa-lightbulb mr-2"></i>
                                    Using the
                                    <a
                                      href="https://mgscloud.marriott.com/mgs/marrdocs/mgs/common/salesmktgrevmgmt/marketing/photography/photoshoots/photographyworksheetdac.xlsx"
                                      download={true}
                                      className="text-red-400 underline px-2"
                                    >
                                      DAC Worksheet (XLS)
                                    </a>
                                    , prepare the required asset metadata with
                                    <a
                                      href="https://mgscloud.marriott.com/mgs/marrdocs/mgs/common/salesmktgrevmgmt/ecommerce/digitalassets/captionalttextwriting.pdf"
                                      download={true}
                                      className="text-red-400 underline px-2"
                                    >
                                      Caption & Alt Text Writing Standards (PDF)
                                    </a>
                                    before uploading your assets to DAC.
                                  </div>
                                  <div className="instruction-note mt-4 mb-4">
                                    <ul>
                                      <li>
                                        Where subscribed to MDS services, the
                                        local Client Community will upload to
                                        DAC.
                                      </li>
                                      <li>
                                        Where property user uploads the images
                                        to DAC, follow the{" "}
                                        <a
                                          href="https://mgscloud.marriott.com/common/sales-mktg-and-rev-mgmt/ecommerce/digital-asset-hub/photography#submissionprocess"
                                          download={true}
                                          className="text-red-400 underline px-2"
                                        >
                                          DAC Submission Process (PDF)
                                        </a>
                                        .
                                      </li>
                                      <li>
                                        For lifestyle images, please ensure the
                                        appropriate model/associate/minor
                                        releases are submitted to DAC.
                                      </li>
                                    </ul>
                                  </div>
                                </>
                              )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PhotoshootDetails;
